import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Mic, Square, Anchor } from "lucide-react";
import {
  CAFE_NAME as DEFAULT_CAFE_NAME,
  LENSES,
  GROUNDING_PROMPTS,
  BEVERAGE_PAUSE_PROMPTS,
  pick,
} from "../data/constants";
import { PauseAnimation, PAUSE_VARIANTS } from "./PauseAnimations";
import { SomaSeal, SunDivider, LensIcon } from "./SomaMarks";
import TerminalQuote from "./TerminalQuote";
import TarotCardArt from "./TarotCardArt";
import JournalCaptureBox from "./JournalCaptureBox";
import VolumeIIPanel from "./VolumeIIPanel";
import ThreeWords from "./ThreeWords";
import SeaBubbles from "./SeaBubbles";
import RitualNote from "./RitualNote";
import FirstCupTip from "./FirstCupTip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Fade in section as it enters viewport */
function FadeSection({ children, id, className = "", testId, threshold = 0.18 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);
  return (
    <section
      ref={ref}
      id={id}
      data-testid={testId}
      className={`section fade-section ${visible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
}

function ScrollCue({ label = "scroll", testId, onClick }) {
  return (
    <div className="scroll-cue" data-testid={testId} onClick={onClick}>
      ↓ {label} ↓
    </div>
  );
}

export default function SomaJourney({ cafe }) {
  /* cafe: { name, slug, custom_note } | null (default brand) */
  const CAFE_NAME = cafe?.name || DEFAULT_CAFE_NAME;
  const CAFE_SLUG = cafe?.slug || null;
  const CUSTOM_NOTE = cafe?.custom_note || null;

  /* Track click once on mount if we're on a cafe-slug page */
  useEffect(() => {
    if (!CAFE_SLUG) return;
    axios.post(`${API}/track/click`, { slug: CAFE_SLUG }).catch(() => {});
  }, [CAFE_SLUG]);

  /* ---------- state ---------- */
  const [pauseText] = useState(() => pick(BEVERAGE_PAUSE_PROMPTS));
  const [groundingPrompt, setGroundingPrompt] = useState("");
  const [pauseVariant] = useState(() => pick(PAUSE_VARIANTS));

  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [base64Image, setBase64Image] = useState(null);
  const [mediaType, setMediaType] = useState(null);

  const [chosenLens, setChosenLens] = useState(null);
  const [lockedDeck, setLockedDeck] = useState(false);

  const [pauseProgress, setPauseProgress] = useState(0);
  const [pauseDone, setPauseDone] = useState(false);

  const [teaserText, setTeaserText] = useState("");
  const [teaserError, setTeaserError] = useState(null);
  const [teaserLoaded, setTeaserLoaded] = useState(false);

  const [readingText, setReadingText] = useState("");
  const [pullQuote, setPullQuote] = useState("");
  const [journalPrompts, setJournalPrompts] = useState([]);
  const [unlocked, setUnlocked] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState(null);

  // 28-day pass checkout (separate from the single-reading unlock flow)
  const [passLoading, setPassLoading] = useState(false);
  const [passError, setPassError] = useState(null);
  // Volume II pass token (persisted in localStorage). When set, the daily reading
  // flow becomes the full/express scroll journey (Phase 5.2 "ubiquitous flow").
  const [volumeIIToken, setVolumeIIToken] = useState(() => {
    try { return localStorage.getItem(VolumeIIPanel.TOKEN_KEY) || null; } catch { return null; }
  });
  // Fetched pass state: { current_day, total_days, today_completed, readings, ... }
  const [passState, setPassState] = useState(null);
  const [passStateLoading, setPassStateLoading] = useState(false);
  // Track whether we just submitted a Vol II reading this session (to keep showing
  // the completion + library card instead of the hub on refresh-avoiding navigations)
  const [passJustCompleted, setPassJustCompleted] = useState(false);
  // Day 28 arc keepsake ("your month, in one thread")
  const [arcData, setArcData] = useState(null);
  const [arcLoading, setArcLoading] = useState(false);
  const [arcError, setArcError] = useState(null);

  const [reflectMode, setReflectMode] = useState("text"); // text | voice
  const [reflectionText, setReflectionText] = useState("");
  const [shareConsent, setShareConsent] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [reflectionSent, setReflectionSent] = useState(false);

  // NEW journey-context state (all optional)
  const [wordsBefore, setWordsBefore] = useState(["", "", ""]);
  const [wordsAfter, setWordsAfter] = useState(["", "", ""]);
  const [macroNoticing, setMacroNoticing] = useState("");
  const [cupName, setCupName] = useState("");
  const [whatsOnMind, setWhatsOnMind] = useState("");
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderSaved, setReminderSaved] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  /* ---------- Journey mode (split journey: full vs express) ---------- */
  const [journeyMode, setJourneyMode] = useState(null); // null until landing CTA picked: "full" | "express"
  const isExpress = journeyMode === "express";
  // In express the "what's on your mind" textarea is collapsed by default;
  // in full mode it's always visible.
  const [mindOpen, setMindOpen] = useState(true);
  useEffect(() => {
    setMindOpen(!isExpress);
  }, [isExpress]);

  /* ---------- Accessibility mode (high-contrast + sans-serif, persisted) ---------- */
  const [a11yMode, setA11yMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("soma-a11y") === "1";
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("a11y-on", a11yMode);
    try { window.localStorage.setItem("soma-a11y", a11yMode ? "1" : "0"); } catch (e) { /* ignore */ }
  }, [a11yMode]);

  /* ---------- refs for sections (for scroll) ---------- */
  const refs = {
    checkin: useRef(null),
    macro: useRef(null),
    upload: useRef(null),
    cards: useRef(null),
    pause: useRef(null),
    reading: useRef(null),
    share: useRef(null),
    reflect: useRef(null),
    history: useRef(null),
    keepsake: useRef(null),
    upsell: useRef(null),
  };
  const scrollTo = (key) => {
    const node = refs[key]?.current;
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* ---------- upload ---------- */
  const handleFile = useCallback((file) => {
    if (!file) return;
    setMediaType(file.type || "image/jpeg");
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImageDataUrl(dataUrl);
      setBase64Image(dataUrl.split(",")[1]);
    };
    reader.readAsDataURL(file);
  }, []);

  /* ---------- after image picked: reveal cards ---------- */
  useEffect(() => {
    if (imageDataUrl) {
      // small delay then scroll into cards
      setTimeout(() => scrollTo("cards"), 500);
    }
  }, [imageDataUrl]);

  /* ---------- choose lens ---------- */
  const chooseLens = (lens) => {
    if (lockedDeck) return;
    setLockedDeck(true);
    setChosenLens(lens);
    setGroundingPrompt(pick(GROUNDING_PROMPTS));
    setTimeout(() => scrollTo("pause"), 700);
    // begin pause + teaser fetch
    setTimeout(() => startPause(lens), 1000);
  };

  /* ---------- 30-second pause + teaser fetch ---------- */
  const startPause = (lens) => {
    setPauseProgress(0);
    setPauseDone(false);
    setTeaserText("");
    setTeaserError(null);
    setTeaserLoaded(false);

    // Express mode uses a short ~7s "siren is reading" beat so it doesn't feel
    // jarringly fast; full mode keeps the 30s meditation floor.
    const totalSeconds = isExpress ? 7 : 30;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += 0.1;
      const p = Math.min(elapsed / totalSeconds, 1);
      setPauseProgress(p);
      if (elapsed >= totalSeconds) {
        clearInterval(interval);
        setPauseDone(true);
      }
    }, 100);

    // kick off teaser request
    fetchTeaser(lens);
  };

  const fetchTeaser = async (lens) => {
    try {
      const res = await axios.post(`${API}/reading`, {
        base64Image,
        mediaType,
        lensInstruction: lens.instruction,
        mode: "teaser",
        cafe_slug: CAFE_SLUG,
        whatsOnMind: whatsOnMind.trim() || null,
      });
      setTeaserText(res.data.reading);
      setTeaserLoaded(true);
    } catch (err) {
      console.error(err);
      setTeaserError(err.response?.data?.detail || err.message || "Could not gather the cup's first noticing.");
      setTeaserLoaded(true);
    }
  };

  /* ---------- unlock full reading ---------- */
  // Stripe-backed flow (sessionStorage-free, mobile-safe):
  //   1. POST /api/checkout/session WITH the reading inputs (base64Image, mediaType, lens, etc.).
  //      Backend persists those alongside the pending transaction so they survive any browser hop.
  //   2. Redirect to Stripe. User pays (or enters a 100%-off promo code).
  //   3. Stripe redirects back to /?session_id=...&paid=1.
  //   4. The mount-time effect below detects this, polls /api/checkout/status, and reads the inputs
  //      back from `transaction.reading_inputs` — no client-side storage needed (mobile Safari has
  //      a ~5MB sessionStorage cap which real cup photos blow past, throwing DOMException
  //      "The quota has been exceeded.").
  //   5. Frontend calls /api/reading with those inputs and unlocks the section.

  const unlockReading = async () => {
    if (!base64Image) return;
    setUnlockLoading(true);
    setUnlockError(null);

    // Pass mode: reading is included in the 28-day pass — call the pass endpoint
    // directly, no Stripe checkout. This mirrors the VolumeIIPanel submit flow
    // but keeps the user inside the full immersive scroll journey.
    if (isPassMode) {
      try {
        const { data } = await axios.post(`${API}/passes/${volumeIIToken}/reading`, {
          base64Image,
          mediaType,
          lensInstruction: chosenLens?.instruction || "",
          whatsOnMind: whatsOnMind.trim() || null,
          cupName: cupName.trim() || null,
          lensLabel: chosenLens?.label || null,
          mode: journeyMode || "full",
        });
        setReadingText(data.reading);
        setPullQuote(data.pull_quote || data.reading);
        setJournalPrompts(Array.isArray(data.journal_prompts) ? data.journal_prompts : []);
        setUnlocked(true);
        setPassJustCompleted(true);
        if (data.state) setPassState(data.state);
        setTimeout(() => {
          const el = document.querySelector('[data-testid="sec-reading"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      } catch (err) {
        setUnlockError(err.response?.data?.detail || err.message || "Could not bring your day into focus.");
      } finally {
        setUnlockLoading(false);
      }
      return;
    }

    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        package_id: "single_reading",
        cafe_slug: CAFE_SLUG,
        origin_url: window.location.origin,
        return_path: window.location.pathname,
        client_ref: chosenLens?.name || null,
        reading_inputs: {
          base64Image,
          mediaType,
          lensInstruction: chosenLens?.instruction || "",
          chosenLensName: chosenLens?.name || null,
          whatsOnMind: whatsOnMind.trim() || null,
        },
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err) {
      setUnlockError(err.response?.data?.detail || err.message || "Could not start checkout.");
      setUnlockLoading(false);
    }
  };

  // 28-day pass — includes the intro reading so it can be seeded as Day 1 (making
  // the very first cup they upload inside the pass naturally become Day 2).
  const beginVolumeII = async () => {
    setPassLoading(true);
    setPassError(null);
    try {
      const intro = (unlocked && readingText)
        ? {
            reading: readingText,
            pull_quote: pullQuote || null,
            journal_prompts: journalPrompts || [],
            lens: chosenLens?.label || null,
            cup_name: null,
            completed_at: new Date().toISOString(),
          }
        : null;
      const { data } = await axios.post(`${API}/checkout/session`, {
        package_id: "twentyeight_day_pass",
        cafe_slug: CAFE_SLUG,
        origin_url: window.location.origin,
        return_path: window.location.pathname,
        reading_inputs: intro ? { intro_reading: intro } : null,
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err) {
      setPassError(err.response?.data?.detail || err.message || "Could not start checkout.");
      setPassLoading(false);
    }
  };

  // After a paid round-trip, finalize: poll Stripe status, then call /api/reading.
  const [paymentPolling, setPaymentPolling] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");

    // Strip session_id/canceled/paid from the URL so it's clean once we're done
    const cleanUrl = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete("session_id");
      u.searchParams.delete("paid");
      u.searchParams.delete("canceled");
      window.history.replaceState({}, "", u.pathname + (u.search ? "?" + u.searchParams.toString() : ""));
    };

    if (canceled) {
      setUnlockError("Checkout was canceled. Your reading is still waiting — try again when you're ready.");
      cleanUrl();
      return;
    }
    if (!sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 12;     // ~24s
    const POLL_INTERVAL = 2000;

    setPaymentPolling(true);
    setUnlockLoading(true);

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Branch: 28-day pass purchase → activate pass + transition to Volume II mode
          if (data?.metadata?.package_id === "twentyeight_day_pass") {
            try {
              const act = await axios.post(`${API}/passes/activate`, { session_id: sessionId });
              const token = act.data?.token;
              if (token) {
                try { localStorage.setItem(VolumeIIPanel.TOKEN_KEY, token); } catch { /* private mode is fine */ }
                setVolumeIIToken(token);
              }
            } catch (err) {
              setUnlockError(err.response?.data?.detail || err.message || "Payment cleared but Volume II didn't open. Refresh to retry.");
            }
            setUnlockLoading(false);
            setPaymentPolling(false);
            cleanUrl();
            return;
          }

          // Otherwise: single-reading unlock flow (existing path)
          const inputs = data?.transaction?.reading_inputs;
          if (!inputs?.base64Image) {
            setUnlockError("Payment received but your cup image is missing on our side. Email hello@drinksomalixir.com and we'll honor it.");
            setUnlockLoading(false);
            setPaymentPolling(false);
            cleanUrl();
            return;
          }

          // Rehydrate the journey so the reading section renders
          setBase64Image(inputs.base64Image);
          setMediaType(inputs.mediaType);
          setImageDataUrl(`data:${inputs.mediaType};base64,${inputs.base64Image}`);
          if (inputs.chosenLensName) {
            const lens = LENSES.find((l) => l.name === inputs.chosenLensName);
            if (lens) setChosenLens(lens);
          }
          setTeaserLoaded(true);
          setPauseDone(true);
          if (journeyMode === null) setJourneyMode("full");

          // Generate the full reading now that the payment cleared
          try {
            const res = await axios.post(`${API}/reading`, {
              base64Image: inputs.base64Image,
              mediaType: inputs.mediaType,
              lensInstruction: inputs.lensInstruction,
              mode: "full",
              cafe_slug: CAFE_SLUG,
              whatsOnMind: inputs.whatsOnMind,
            });
            setReadingText(res.data.reading);
            setPullQuote(res.data.pullQuote || res.data.reading);
            setJournalPrompts(Array.isArray(res.data.journalPrompts) ? res.data.journalPrompts : []);
            setUnlocked(true);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="sec-reading"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 800);
          } catch (err) {
            setUnlockError(err.response?.data?.detail || err.message || "Payment cleared but the reading couldn't be summoned. Refresh to retry.");
          } finally {
            setUnlockLoading(false);
            setPaymentPolling(false);
            cleanUrl();
          }
          return;
        }
        if (data.status === "expired" || (data.status === "complete" && data.payment_status === "unpaid")) {
          setUnlockError("Checkout expired. Try again whenever you're ready.");
          setUnlockLoading(false);
          setPaymentPolling(false);
          cleanUrl();
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setUnlockError("Still confirming your payment — refresh in a moment and your reading will surface.");
          setUnlockLoading(false);
          setPaymentPolling(false);
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (attempts >= MAX_ATTEMPTS) {
          setUnlockError(err.response?.data?.detail || err.message || "Could not confirm payment.");
          setUnlockLoading(false);
          setPaymentPolling(false);
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  /* ---------- Volume II pass state fetch (Phase 5.2 ubiquitous flow) ----------
   * When a pass token is present, load the pass state so the journey knows the
   * current day, whether today is complete, and past days' library. */
  const refreshPassState = useCallback(async (tok) => {
    const t = tok || volumeIIToken;
    if (!t) return null;
    setPassStateLoading(true);
    try {
      const { data } = await axios.get(`${API}/passes/${t}`);
      setPassState(data);
      return data;
    } catch (err) {
      // 404 → token is invalid; clear it so user goes back to normal flow
      if (err.response?.status === 404) {
        try { localStorage.removeItem(VolumeIIPanel.TOKEN_KEY); } catch { /* ignore */ }
        setVolumeIIToken(null);
        setPassState(null);
      }
      return null;
    } finally {
      setPassStateLoading(false);
    }
  }, [volumeIIToken]);

  useEffect(() => {
    if (volumeIIToken && !passState) refreshPassState(volumeIIToken);
  }, [volumeIIToken, passState, refreshPassState]);

  // Day 28 arc keepsake — lazy-fetched when user hits the "reveal your arc" button.
  // Backend caches it, so re-clicks are cheap.
  const revealArc = useCallback(async () => {
    if (!volumeIIToken) return;
    setArcLoading(true);
    setArcError(null);
    try {
      const { data } = await axios.get(`${API}/passes/${volumeIIToken}/arc`);
      setArcData(data);
    } catch (err) {
      setArcError(err.response?.data?.detail || err.message || "Couldn't compose your arc yet.");
    } finally {
      setArcLoading(false);
    }
  }, [volumeIIToken]);

  // Pass mode = active pass, today's slot is open, so the journey renders the
  // full immersive daily flow (paywall + Vol II upsell hidden, reading generated
  // via the pass endpoint for free).
  const isPassMode = !!volumeIIToken && !!passState && !passState.today_completed;
  // Hub mode = active pass AND today already complete AND we're NOT in a session
  // that just finished today's reading (which stays on the completion card).
  const isPassHub = !!volumeIIToken && !!passState && passState.today_completed && !passJustCompleted && !unlocked;

  /* ---------- share / save ---------- */
  const shareCanvasBlob = useRef(null);
  useEffect(() => {
    if (!unlocked || !imageDataUrl || !readingText) return;
    buildShareCanvas();
  }, [unlocked, imageDataUrl, readingText]);

  const buildShareCanvas = () => {
    const canvas = document.createElement("canvas");
    const W = 640;
    const ctx = canvas.getContext("2d");
    const sidePad = 60;
    ctx.font = "italic 19px 'IM Fell DW Pica', Georgia";
    const lines = wrapText(ctx, readingText, W - sidePad * 2);
    const lineHeight = 27;
    const photoSize = 220, frameOuterPad = 14, frameBottomPad = 34;
    const frameW = photoSize + frameOuterPad * 2;
    const frameH = photoSize + frameOuterPad + frameBottomPad;
    const topMargin = 60;
    const frameTop = topMargin;
    const lensTagTop = frameTop + frameH + 38;
    const textTop = lensTagTop + 36;
    const textBlockH = lines.length * lineHeight;
    const footerTop = textTop + textBlockH + 28;
    const H = footerTop + 50;
    canvas.width = W;
    canvas.height = H;
    ctx.fillStyle = "#050B1B";
    ctx.fillRect(0, 0, W, H);

    const frameX = (W - frameW) / 2;
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = "#E1E7E5";
    ctx.fillRect(frameX, frameTop, frameW, frameH);
    ctx.restore();

    const img = new Image();
    img.onload = () => {
      const px = frameX + frameOuterPad, py = frameTop + frameOuterPad;
      ctx.save();
      ctx.beginPath();
      ctx.rect(px, py, photoSize, photoSize);
      ctx.clip();
      const scale = Math.max(photoSize / img.width, photoSize / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      ctx.drawImage(img, px + (photoSize - dw) / 2, py + (photoSize - dh) / 2, dw, dh);
      ctx.fillStyle = "#0B142A";
      ctx.globalAlpha = 0.1;
      ctx.fillRect(px, py, photoSize, photoSize);
      ctx.globalAlpha = 1;
      ctx.restore();

      ctx.fillStyle = "#3A7CA5";
      ctx.font = "10px 'Space Mono', monospace";
      ctx.textAlign = "center";
      ctx.fillText("// " + chosenLens.label.toUpperCase(), W / 2, lensTagTop);

      ctx.fillStyle = "#E8EEF5";
      ctx.font = "italic 19px 'IM Fell DW Pica', Georgia";
      let ty = textTop;
      lines.forEach((line) => { ctx.fillText(line, W / 2, ty); ty += lineHeight; });

      ctx.fillStyle = "#5A6A82";
      ctx.font = "10px 'Space Mono', monospace";
      ctx.fillText(CAFE_NAME.toUpperCase() + " × SOMA LIXIR", W / 2, footerTop);

      canvas.toBlob((blob) => { shareCanvasBlob.current = blob; }, "image/png");
    };
    img.src = imageDataUrl;
  };

  const handleShare = async () => {
    if (!shareCanvasBlob.current) return;
    const file = new File([shareCanvasBlob.current], "soma-lixir-reading.png", { type: "image/png" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "My Soma Lixir reading" }); } catch (e) {}
    } else {
      downloadShare();
    }
  };
  const downloadShare = () => {
    if (!shareCanvasBlob.current) return;
    const url = URL.createObjectURL(shareCanvasBlob.current);
    const a = document.createElement("a");
    a.download = "soma-lixir-reading.png";
    a.href = url;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Send-to-someone — like share but with a thoughtful prefilled message
  const handleSendToSomeone = async () => {
    const cafePath = CAFE_SLUG ? `/c/${CAFE_SLUG}` : "/";
    const link = `${window.location.origin}${cafePath}`;
    const message = `I just did this and it reminded me of you ↓\n\n"${pullQuote || readingText}"\n\n${link}`;
    if (shareCanvasBlob.current && navigator.canShare?.({ files: [new File([shareCanvasBlob.current], "soma.png", { type: "image/png" })] })) {
      try {
        const file = new File([shareCanvasBlob.current], "soma-lixir-reading.png", { type: "image/png" });
        await navigator.share({ files: [file], text: message, title: "A small moment for you" });
        return;
      } catch (e) { /* user cancelled or no permission */ }
    }
    if (navigator.share) {
      try {
        await navigator.share({ text: message, title: "A small moment for you" });
        return;
      } catch (e) {}
    }
    // Final fallback — copy to clipboard
    try {
      await navigator.clipboard.writeText(message);
      alert("Copied to your clipboard — paste it into iMessage, email, or wherever ✨");
    } catch (e) {
      alert("Couldn't open the share sheet — please try Save instead.");
    }
  };

  // Save the reminder email opt-in (no email sent yet — captured for future Resend wiring)
  const saveReminder = async () => {
    if (!reminderEmail.trim()) return;
    try {
      await axios.post(`${API}/reminder`, {
        email: reminderEmail.trim(),
        cafe_slug: CAFE_SLUG,
      });
      setReminderSaved(true);
    } catch (err) {
      setReminderSaved(true); // graceful fallback — don't block
    }
  };

  /* ---------- voice memo ---------- */
  const toggleRecording = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStreamRef.current = stream;
        const mr = new MediaRecorder(stream);
        audioChunksRef.current = [];
        mr.ondataavailable = (e) => audioChunksRef.current.push(e.data);
        mr.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          setRecordedBlobUrl(URL.createObjectURL(blob));
          audioStreamRef.current?.getTracks().forEach((t) => t.stop());
        };
        mediaRecorderRef.current = mr;
        mr.start();
        setIsRecording(true);
      } catch (err) {
        alert("Couldn't access microphone: " + err.message);
      }
    } else {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
  };

  /* ---------- submit reflection ---------- */
  const submitReflection = async () => {
    try {
      await axios.post(`${API}/reflection`, {
        cafe: CAFE_NAME,
        cafe_slug: CAFE_SLUG,
        lens: chosenLens.label,
        reading: readingText,
        reflectionType: reflectMode,
        reflectionText: reflectMode === "text" ? reflectionText.trim() : null,
        hasVoiceMemo: reflectMode === "voice" && recordedBlobUrl !== null,
        shareConsent,
        notifyEmail: shareConsent ? notifyEmail.trim() || null : null,
        cupName: cupName.trim() || null,
        whatsOnMind: whatsOnMind.trim() || null,
        macroNoticing: macroNoticing.trim() || null,
        wordsBefore: wordsBefore.some(w => w.trim()) ? wordsBefore.map(w => w.trim()) : null,
        wordsAfter: wordsAfter.some(w => w.trim()) ? wordsAfter.map(w => w.trim()) : null,
      });
      setReflectionSent(true);
    } catch (err) {
      console.error(err);
      setReflectionSent(true); // graceful fallback — don't block the flow
    }
  };

  /* ---------- render ---------- */
  const lensReadingHeadline = chosenLens ? `Your ${chosenLens.label} reading is ready.` : "Your reading is ready.";

  // Volume II hub: only used when the user has already completed today's
  // reading. Shows their progress + library + "come back tomorrow" gate.
  // Fresh days route through the full immersive journey below (Phase 5.2).
  if (isPassHub) {
    return (
      <div className="page" data-testid="soma-page-volume-ii">
        <div className="grain" />
        <div className="vignette" />
        <SeaBubbles count={18} />
        <div className="scanlines" />
        <VolumeIIPanel
          token={volumeIIToken}
          onSignOut={() => { setVolumeIIToken(null); setPassState(null); }}
        />
      </div>
    );
  }

  // While pass state is loading on first render, show a soft placeholder so we
  // don't briefly flash the normal "buy Vol II" flow to an active pass holder.
  if (volumeIIToken && !passState && passStateLoading) {
    return (
      <div className="page" data-testid="soma-page-volume-ii-loading">
        <div className="grain" />
        <div className="vignette" />
        <SeaBubbles count={18} />
        <div className="scanlines" />
        <div style={{ padding: "80px 22px", textAlign: "center" }}>
          <p style={{ color: "var(--cream-soft)", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic" }}>opening Volume II…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="soma-page">
      <div className="grain" />
      <div className="vignette" />
      <SeaBubbles count={18} />
      <div className="scanlines" />
      <FirstCupTip />
      <button
        type="button"
        data-testid="a11y-toggle"
        aria-pressed={a11yMode}
        aria-label={a11yMode ? "Turn off accessibility mode" : "Turn on accessibility mode"}
        onClick={() => setA11yMode((v) => !v)}
        className={`a11y-pill${a11yMode ? " is-on" : ""}`}
      >
        <span className="a11y-pill-aa" aria-hidden="true">Aa</span>
        <span className="a11y-pill-label">{a11yMode ? "easy-read on" : "easy-read"}</span>
      </button>

      {/* 1. LANDING */}
      <FadeSection id="sec-landing" testId="sec-landing">
        <div style={{ textAlign: "center" }}>
          <img
            src="/siren-cup.png"
            alt=""
            data-testid="siren-hero"
            className="siren-illustration siren-hero-anim"
            style={{
              display: "block",
              width: 170,
              height: "auto",
              margin: "4px auto 8px",
              opacity: 0.92,
            }}
          />
          {isPassMode ? (
            <>
              <p className="section-marker" style={{ textAlign: "center" }} data-testid="pass-day-marker">
                volume ii — day {passState.current_day} of {passState.total_days}
              </p>
              <h1 className="brand-wordmark" style={{ fontSize: 60, lineHeight: 1, margin: "8px 0 0", color: "var(--cream)" }}>
                Day{" "}
                <span style={{ fontStyle: "italic", color: "var(--terracotta-soft)" }} data-testid="pass-day-number">
                  {String(passState.current_day).padStart(2, "0")}
                </span>
              </h1>
              <p style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.36em",
                textTransform: "uppercase",
                color: "var(--terracotta-soft)",
                margin: "10px 0 0",
                opacity: 0.85,
              }}>
                ⌁ a new cup, a new noticing ⌁
              </p>
              {/* Progress dots */}
              <div className="keepsake-map" data-testid="pass-progress-dots" style={{ marginTop: 18 }}>
                {Array.from({ length: passState.total_days }).map((_, i) => {
                  const dayNum = i + 1;
                  const done = passState.readings.some((r) => r.day === dayNum);
                  const isCurrent = dayNum === passState.current_day && !done;
                  return (
                    <span
                      key={i}
                      className={`map-dot ${done ? "map-dot-active" : ""}${isCurrent ? " map-dot-current" : ""}`}
                      title={done ? `Day ${dayNum}` : ""}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <p className="section-marker" style={{ textAlign: "center" }}>00 — begin</p>
              <h1 className="brand-wordmark" style={{ fontSize: 60, lineHeight: 1, margin: "8px 0 0", color: "var(--cream)" }}>
                SOMA{" "}
                <span style={{ fontStyle: "italic", color: "var(--terracotta-soft)" }}>Lixir</span>
              </h1>
              <p style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.36em",
                textTransform: "uppercase",
                color: "var(--terracotta-soft)",
                margin: "10px 0 0",
                opacity: 0.85,
              }}>
                ⌁ a siren's reading ⌁
              </p>
            </>
          )}
          {CAFE_SLUG && (
            <div
              data-testid="cafe-attribution-pin"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginTop: 14,
                padding: "6px 12px",
                background: "rgba(143, 196, 224, 0.08)",
                border: "1px solid rgba(143, 196, 224, 0.35)",
                borderRadius: 0,
                fontFamily: "'Space Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--cream-soft)",
              }}
            >
              <Anchor size={12} strokeWidth={1.6} style={{ color: "var(--terracotta-soft)" }} />
              <span>from the deep · <span style={{ color: "var(--cream)" }}>{CAFE_NAME}</span></span>
            </div>
          )}
          <SunDivider width={120} />
          {isPassMode ? (
            <>
              <p className="body-copy" style={{ marginTop: 24 }} data-testid="pass-day-lead">
                Welcome back. Another cup, another day. Enjoy a deep inhale and exhale before we begin.
              </p>
              <p className="body-copy">
                {passState.current_day === 2
                  ? "You've done this once already — Day 01 is already in your library, downstream. Today's cup will land beside it."
                  : `You've been at this ${passState.readings.length} day${passState.readings.length === 1 ? "" : "s"} now. The shapes are starting to line up, whether you can see it yet or not.`}
              </p>
              <p className="body-copy" style={{ marginBottom: 24 }}>
                Same ritual as always — finish your drink, take a photo of what's left, and we'll look at the grounds together.
              </p>
              {passState.readings.length > 0 && (
                <div data-testid="pass-mini-library" style={{
                  margin: "8px auto 24px",
                  padding: "12px 14px",
                  maxWidth: 380,
                  background: "rgba(143, 196, 224, 0.05)",
                  border: "1px solid rgba(143, 196, 224, 0.22)",
                  textAlign: "left",
                }}>
                  <p style={{ margin: "0 0 8px", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--terracotta-soft)" }}>
                    // your last cup{passState.readings.length > 1 ? "s" : ""}
                  </p>
                  {passState.readings.slice(-3).reverse().map((r) => (
                    <p
                      key={r.day}
                      data-testid={`pass-mini-library-day-${r.day}`}
                      style={{
                        margin: "6px 0",
                        fontFamily: "'IM Fell DW Pica', serif",
                        fontStyle: "italic",
                        fontSize: 14,
                        color: "var(--cream-soft)",
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ fontFamily: "'VT323', monospace", fontSize: 16, color: "var(--terracotta-soft)", marginRight: 8 }}>
                        {String(r.day).padStart(2, "0")}
                      </span>
                      {r.pull_quote ? `"${r.pull_quote}"` : (r.cup_name || "(no quote captured)")}
                    </p>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="body-copy" style={{ marginTop: 24 }}>Welcome. Enjoy a deep inhale and exhale before we begin.</p>
              <p className="body-copy">
                In front of you is a cup you've nearly finished — the last of the warmth, the grounds settling at the bottom. For the next three minutes, that cup is the only thing you need to tend to.
              </p>
              <p className="body-copy">
                What you're about to do is older than your great-grandmother's great-grandmother. Reading the grounds or tea leaves left in a cup — carried through Ottoman parlors, European kitchens, generations of people pausing to look closely at what's left behind.
              </p>
              <p className="body-copy" style={{ marginBottom: 24 }}>
                This isn't about predicting your future. It's an invitation to notice your own inner world — the same way looking at clouds or candle wax has always given us permission to think out loud.
              </p>
            </>
          )}
        </div>
        {!isPassMode && (
          <>
            <TerminalQuote
              text="The tradition never claimed the cup held power. The leaves only gave your own knowing somewhere to land — a shape to think out loud against."
              label="SOMA://TRADITION.LOG"
              testId="tidbit-tradition"
            />
            <p className="body-copy" style={{ fontStyle: "italic", color: "var(--dim)", textAlign: "center", fontSize: 13, marginTop: 24 }}>
              So stay a moment. These small, intentional pauses are so good for your nervous system. Every moment deserves to be cherished — even a "dirty" coffee cup, right before you rinse it away.
            </p>
          </>
        )}
        {CUSTOM_NOTE && !isPassMode && (
          <div
            data-testid="cafe-custom-note"
            className="paper-card"
            style={{ marginTop: 36, position: "relative" }}
          >
            <span className="wax-seal">★</span>
            <p className="paper-card-title">{CAFE_NAME}</p>
            <p className="italic-quote" style={{ fontSize: 19, color: "var(--ink)", lineHeight: 1.45, margin: "16px 0 0", textAlign: "center" }}>
              "{CUSTOM_NOTE}"
            </p>
          </div>
        )}
        {/* Two-mode entry: full journey vs. express reading */}
        <div className="journey-mode-picker" data-testid="journey-mode-picker">
          <p className="journey-mode-prompt">
            {isPassMode ? `how do you want to do day ${passState.current_day}?` : "how do you want to do this today?"}
          </p>
          <div className="journey-mode-buttons">
            <button
              type="button"
              className="journey-mode-btn primary"
              data-testid="cta-begin-full"
              aria-label={isPassMode ? `Begin day ${passState.current_day} — full experience, about 3 to 4 minutes` : "Begin the journey — full experience, about 3 to 4 minutes"}
              onClick={() => { setJourneyMode("full"); setTimeout(() => scrollTo("checkin"), 220); }}
            >
              <span className="journey-mode-btn-title">
                {isPassMode ? `Begin day ${passState.current_day}` : "Begin the journey"}
              </span>
              <span className="journey-mode-btn-sub">~3–4 min · the full experience</span>
            </button>
            <button
              type="button"
              className="journey-mode-btn secondary"
              data-testid="cta-begin-express"
              aria-label="Express reading — about 1 minute, straight to the cup"
              onClick={() => { setJourneyMode("express"); setTimeout(() => scrollTo("checkin"), 220); }}
            >
              <span className="journey-mode-btn-title">Express reading</span>
              <span className="journey-mode-btn-sub">~1 min · straight to the cup</span>
            </button>
          </div>
        </div>
      </FadeSection>

      {/* 2. CHECK-IN */}
      <div ref={refs.checkin}>
        <FadeSection id="sec-checkin" testId="sec-checkin">
          <p className="section-marker">01 — a small question</p>
          <h2 className="italic-quote" style={{ fontSize: 32, textAlign: "center", lineHeight: 1.25, margin: "12px 0 14px", color: "var(--cream)" }}>
            Have you finished your drink yet?
          </h2>
          <RitualNote />
          {isExpress ? (
            <p className="body-copy" style={{ textAlign: "center", color: "var(--cream-soft)", fontSize: 16, margin: "12px auto 28px", maxWidth: 320, lineHeight: 1.55, fontStyle: "italic" }}>
              No rush — finish what's left, then we'll look into the cup together.
            </p>
          ) : (
            <>
              <p className="body-copy" style={{ textAlign: "center", color: "var(--gold)", fontSize: 13, letterSpacing: "0.05em" }}>
                If not, enjoy this for a moment:
              </p>
              <p className="italic-quote" style={{ fontSize: 21, textAlign: "center", maxWidth: 340, margin: "30px auto", lineHeight: 1.55, color: "var(--cream)" }}>
                {pauseText}
              </p>
              <TerminalQuote
                text="Across every culture that kept this practice, the bones were the same: finish the drink, sit with what's left, look closely before you rinse it away."
                label="SOMA://CULTURES.LOG"
                testId="tidbit-cultures"
              />
              {/* Three words BEFORE — gentle mood baseline */}
              <div style={{ marginTop: 38 }} data-testid="threewords-before-wrap">
                <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 17, textAlign: "center", color: "var(--cream-soft)", margin: "0 0 14px" }}>
                  If three words could describe how you're arriving today, what would they be?
                </p>
                <ThreeWords value={wordsBefore} onChange={setWordsBefore} testIdPrefix="threewords-before" />
                <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textAlign: "center", color: "var(--dim)", margin: "10px 0 0", textTransform: "uppercase" }}>
                  optional · we'll ask again at the end
                </p>
              </div>
            </>
          )}
          <ScrollCue
            label={isExpress ? "now, your cup" : "keep going"}
            testId="cue-checkin"
            onClick={() => scrollTo(isExpress ? "upload" : "macro")}
          />
        </FadeSection>
      </div>

      {/* 3. MACRO MOMENT — full journey only */}
      {!isExpress && (
      <div ref={refs.macro}>
        <FadeSection id="sec-macro" testId="sec-macro">
          <p className="section-marker">02 — a macro moment</p>
          <p className="body-copy" style={{ marginTop: 24, textAlign: "center" }}>
            Here's something true about your eyes: looking at something close up — like this phone screen, or what we're about to do with your cup — switches your nervous system's alert mode into the hyper-present, the same low simmer of focus your body uses for anything nearby and immediate. Looking far into the distance does the opposite, that's one of the reasons why staring at sunsets feels so nice. It tells your body there's nothing to react to right now, and lets it settle in.
          </p>
          <p className="italic-quote" style={{ fontSize: 19, textAlign: "center", color: "var(--cream)", margin: "26px auto", maxWidth: 340, lineHeight: 1.5 }}>
            So before you zoom in on the micro of your cup — find something far away. Out a window, across the room, to the end of the street. Let your eyes rest there for just a few slow breaths.
          </p>
          {/* Macro noticing reflection */}
          <div style={{ marginTop: 12 }} data-testid="macro-noticing-wrap">
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "var(--terracotta-soft)", textTransform: "uppercase", marginBottom: 10, textAlign: "center" }}>
              // if something stood out
            </p>
            <textarea
              data-testid="macro-noticing-input"
              value={macroNoticing}
              onChange={(e) => setMacroNoticing(e.target.value)}
              placeholder="a tree, a stranger, a corner of the ceiling… what did you find? did anything shift?"
              rows={2}
              style={{
                width: "100%",
                border: "1px solid rgba(143, 196, 224, 0.32)",
                borderRadius: 0,
                padding: "12px 14px",
                fontFamily: "'IM Fell DW Pica', serif",
                fontStyle: "italic",
                fontSize: 16,
                color: "var(--cream)",
                background: "rgba(255, 255, 255, 0.025)",
                outline: "none",
                resize: "vertical",
                lineHeight: 1.5,
                transition: "border-color 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--terracotta-soft)")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(143, 196, 224, 0.32)")}
            />
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textAlign: "right", color: "var(--dim)", margin: "6px 0 0", textTransform: "uppercase" }}>
              optional · scroll past to skip
            </p>
          </div>
          <ScrollCue label="now, your cup" testId="cue-macro" onClick={() => scrollTo("upload")} />
        </FadeSection>
      </div>
      )}

      {/* 4. UPLOAD */}
      <div ref={refs.upload}>
        <FadeSection id="sec-upload" testId="sec-upload">
          <p className="section-marker">04 — your cup</p>
          <p className="body-copy" style={{ textAlign: "center", marginTop: 10, marginBottom: 26, fontSize: 16, color: "var(--terracotta-soft)", fontStyle: "italic" }}>
            Hold your cup directly above and snap straight down, so the whole inside is visible in one frame — good light helps too.
          </p>
          <label className="upload-zone" data-testid="upload-zone">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="upload-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <div className="paper-card" style={{ position: "relative" }}>
              <span className="wax-seal">vol. I</span>
              {!imageDataUrl ? (
                <>
                  <p className="paper-card-title">From the floor of your cup</p>
                  <p className="paper-card-heading">Affix your photograph here</p>
                  <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 14px" }}>
                    <div className="photo-frame" style={{ width: 200, height: 160 }}>
                      <span className="photo-corner tl" />
                      <span className="photo-corner tr" />
                      <span className="photo-corner bl" />
                      <span className="photo-corner br" />
                      <Camera size={26} color="#1E3A5F" style={{ opacity: 0.72 }} />
                    </div>
                  </div>
                  <p className="paper-card-foot">tap, snap, or upload — whatever's left of it</p>
                </>
              ) : (
                <>
                  <p className="paper-card-title">Specimen recorded</p>
                  <div style={{ display: "flex", justifyContent: "center", margin: "16px 0 12px" }}>
                    <div className="photo-frame" style={{ width: 200, height: 200, background: "transparent", padding: 4 }}>
                      <span className="photo-corner tl" />
                      <span className="photo-corner tr" />
                      <span className="photo-corner bl" />
                      <span className="photo-corner br" />
                      <img
                        src={imageDataUrl}
                        alt="your cup"
                        data-testid="upload-preview"
                        style={{ width: "100%", height: "100%", objectFit: "cover", filter: "sepia(0.18) saturate(0.9) brightness(0.96)" }}
                      />
                    </div>
                  </div>
                  {/* Name this cup */}
                  <div style={{ marginTop: 4, padding: "0 4px" }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                    <p style={{ fontFamily: "'IM Fell English', serif", fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--rust)", textAlign: "center", margin: "8px 0 6px" }}>
                      this cup is…
                    </p>
                    <input
                      data-testid="cup-name-input"
                      type="text"
                      maxLength={48}
                      value={cupName}
                      onChange={(e) => setCupName(e.target.value)}
                      placeholder="tuesday morning fog · post-meeting espresso · etc."
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "1px solid rgba(11, 20, 42, 0.35)",
                        padding: "6px 4px",
                        fontFamily: "'IM Fell DW Pica', serif",
                        fontStyle: "italic",
                        fontSize: 17,
                        color: "var(--ink)",
                        background: "transparent",
                        outline: "none",
                        textAlign: "center",
                      }}
                    />
                  </div>
                  <p className="paper-card-foot" style={{ marginTop: 16 }}>tap the photo to choose a different cup</p>
                </>
              )}
            </div>
          </label>
          {imageDataUrl && (
            <ScrollCue label="let's look closely" testId="cue-upload" onClick={() => scrollTo("cards")} />
          )}
        </FadeSection>
      </div>

      {/* 5. CARDS */}
      {imageDataUrl && (
        <div ref={refs.cards}>
          <FadeSection id="sec-cards" testId="sec-cards">
            <p className="section-marker">05 — choose your focus</p>
            <TerminalQuote
              text="A cup is always read for one question at a time. A vague question, the readers said, gave a vague cup. Specificity is a kindness you do yourself."
              label="SOMA://FOCUS.LOG"
              testId="tidbit-focus"
            />
            {/* What's on your mind — personal framing for the AI; collapsible in express */}
            <div style={{ marginTop: 26, marginBottom: 22 }} data-testid="whats-on-mind-wrap">
              {!mindOpen ? (
                <button
                  type="button"
                  data-testid="whats-on-mind-open"
                  onClick={() => setMindOpen(true)}
                  style={{
                    display: "block",
                    margin: "0 auto",
                    background: "transparent",
                    border: "1px dashed rgba(143, 196, 224, 0.32)",
                    color: "var(--terracotta-soft)",
                    padding: "10px 18px",
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  + add what's on your mind <span style={{ opacity: 0.6 }}>(optional)</span>
                </button>
              ) : (
                <>
                  <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "var(--terracotta-soft)", textTransform: "uppercase", marginBottom: 10, textAlign: "center" }}>
                    // in your own words
                  </p>
                  <textarea
                    data-testid="whats-on-mind-input"
                    value={whatsOnMind}
                    onChange={(e) => setWhatsOnMind(e.target.value)}
                    placeholder="if something specific is stirring for you today, name it here."
                    rows={2}
                    maxLength={300}
                    disabled={lockedDeck}
                    style={{
                      width: "100%",
                      border: "1px solid rgba(143, 196, 224, 0.32)",
                      borderRadius: 0,
                      padding: "12px 14px",
                      fontFamily: "'IM Fell DW Pica', serif",
                      fontStyle: "italic",
                      fontSize: 16,
                      color: "var(--cream)",
                      background: "rgba(255, 255, 255, 0.025)",
                      outline: "none",
                      resize: "vertical",
                      lineHeight: 1.5,
                      transition: "border-color 0.3s ease",
                      opacity: lockedDeck ? 0.45 : 1,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "var(--terracotta-soft)")}
                    onBlur={(e) => (e.target.style.borderColor = "rgba(143, 196, 224, 0.32)")}
                  />
                  <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textAlign: "right", color: "var(--dim)", margin: "6px 0 0", textTransform: "uppercase" }}>
                    optional · then pick a focus card below
                  </p>
                </>
              )}
            </div>
            <div className={`lens-grid ${lockedDeck ? "deck-disabled" : ""}`} data-testid="lens-grid">
              {LENSES.map((lens) => {
                const isChosen = chosenLens?.name === lens.name;
                return (
                  <div
                    key={lens.name}
                    className={`lens-slot ${isChosen ? "chosen" : ""}`}
                    onClick={() => chooseLens(lens)}
                    data-testid={`lens-card-${lens.name}`}
                  >
                    <div className={`lens-inner ${isChosen ? "flipped" : ""}`}>
                      <div className="lens-face lens-front" style={{ padding: 0 }}>
                        <TarotCardArt slug={lens.tarot} tarotName={lens.tarotName} lensLabel={lens.label} />
                      </div>
                      <div className="lens-face lens-back">chosen</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </FadeSection>
        </div>
      )}

      {/* 6. PAUSE → TEASER */}
      {chosenLens && (
        <div ref={refs.pause}>
          <FadeSection id="sec-pause" testId="sec-pause">
            <AnimatePresence mode="wait">
              {!(pauseDone && teaserLoaded) ? (
                <motion.div
                  key="waiting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Phased status banner: phase A while the siren is gathering, phase B once she's listening with you */}
                  <div
                    data-testid="brewing-banner"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                      margin: "0 auto 22px",
                      padding: "10px 16px",
                      maxWidth: 340,
                      background: "rgba(143, 196, 224, 0.06)",
                      border: "1px solid rgba(143, 196, 224, 0.28)",
                      fontFamily: "'Space Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.26em",
                      textTransform: "uppercase",
                      color: "var(--terracotta-soft)",
                      transition: "background 0.6s ease, border-color 0.6s ease",
                    }}
                  >
                    {pauseProgress < 0.4 ? (
                      <>
                        <span data-testid="brewing-phase-a">// the siren is reading your cup</span>
                        <span className="brewing-dots" aria-hidden="true">
                          <span>.</span><span>.</span><span>.</span>
                        </span>
                      </>
                    ) : (
                      <span data-testid="brewing-phase-b">// what surfaces is yours alone · stay a moment</span>
                    )}
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <PauseAnimation variant={pauseVariant} progress={pauseProgress} />
                  </div>
                  {!isExpress && (
                    <>
                      <p className="section-marker" style={{ textAlign: "center" }}>06 — the first ripple</p>
                      <p
                        className="italic-quote"
                        data-testid="grounding-prompt"
                        style={{ fontSize: 21, textAlign: "center", color: "var(--cream)", margin: "20px auto 26px", maxWidth: 320, lineHeight: 1.5 }}
                      >
                        {groundingPrompt}
                      </p>
                      <TerminalQuote
                        text="Old readers warned against reading too much. Not every single mark was a message. Most of the cup was simply a cup. The art was knowing which few shapes were speaking."
                        label="SOMA://READERS.LOG"
                        testId="tidbit-readers"
                      />
                    </>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="revealed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9 }}
                >
                  <p className="section-marker" style={{ textAlign: "center" }}>06 — the first ripple</p>
                  {teaserError ? (
                    <p className="error-banner" data-testid="teaser-error" style={{ margin: "20px 0" }}>{teaserError}</p>
                  ) : (
                    <>
                      <p
                        className="italic-quote"
                        data-testid="teaser-text"
                        style={{ fontSize: 22, textAlign: "center", color: "var(--cream)", margin: "26px auto 18px", maxWidth: 360, lineHeight: 1.55 }}
                      >
                        {teaserText}
                      </p>
                      <p
                        data-testid="teaser-hook"
                        style={{
                          fontFamily: "'IM Fell DW Pica', serif",
                          fontStyle: "italic",
                          fontSize: 17,
                          textAlign: "center",
                          color: "var(--terracotta-soft)",
                          margin: "0 auto 28px",
                          maxWidth: 320,
                          letterSpacing: "0.01em",
                        }}
                      >
                        There's a current in this. Follow it down?
                      </p>
                    </>
                  )}
                  <TerminalQuote
                    text="Readers speak of symbols by where they live in the cup, not just what they are. The same shape near the rim and at the base tells two different stories."
                    label="SOMA://SYMBOLS.LOG"
                    testId="tidbit-symbols"
                  />
                  <ScrollCue label="there's more here" testId="cue-teaser" onClick={() => scrollTo("reading")} />
                </motion.div>
              )}
            </AnimatePresence>
          </FadeSection>
        </div>
      )}

      {/* 7. FULL READING + PAYWALL */}
      {chosenLens && teaserLoaded && (
        <div ref={refs.reading}>
          <FadeSection id="sec-reading" testId="sec-reading" className={`gated ${unlocked ? "" : "locked"}`}>
            <p className="section-marker" style={{ textAlign: "center" }}>07 — your reading</p>
            <h2 className="serif-display" style={{ fontSize: 30, textAlign: "center", margin: "8px 0 30px", color: "var(--cream)", fontStyle: "italic" }}>
              {lensReadingHeadline}
            </h2>
            <div className="gated-content">
              <div className="polaroid-wrap" style={{ marginBottom: 26 }}>
                <div className="polaroid-frame">
                  <img src={imageDataUrl} alt="your cup" />
                </div>
              </div>
              <p className="reading-text" data-testid="reading-text">
                {unlocked ? readingText : "Your full reading is gathering shape here — unlock to bring it into focus."}
              </p>
              <p className="brand-footer" style={{ marginTop: 22 }}>{CAFE_NAME} × Soma Lixir</p>
            </div>
            {/* Journal prompts after unlock */}
            {unlocked && journalPrompts && journalPrompts.length > 0 && (
              <div data-testid="journal-prompts" style={{ marginTop: 32 }}>
                <p className="section-marker" style={{ textAlign: "center", marginBottom: 4 }}>three prompts to sit with</p>
                <p style={{ textAlign: "center", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 14, color: "var(--dim)", margin: "0 0 18px" }}>
                  to carry into the next few days
                </p>
                <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 14 }}>
                  {journalPrompts.slice(0, 3).map((p, i) => (
                    <li key={i} data-testid={`journal-prompt-${i}`} style={{
                      display: "flex",
                      gap: 14,
                      alignItems: "flex-start",
                      paddingLeft: 14,
                      borderLeft: "2px solid var(--terracotta-soft)",
                    }}>
                      <span style={{ fontFamily: "'VT323', monospace", fontSize: 26, color: "var(--terracotta-soft)", lineHeight: 1, marginTop: 2, minWidth: 18 }}>
                        {i + 1}
                      </span>
                      <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 17, color: "var(--cream)", lineHeight: 1.55, margin: 0 }}>
                        {p}
                      </p>
                    </li>
                  ))}
                </ol>
                <JournalCaptureBox
                  prompts={journalPrompts.slice(0, 3)}
                  readingExcerpt={pullQuote || (readingText && readingText.slice(0, 380))}
                  cafeSlug={CAFE_SLUG}
                  lens={chosenLens?.label}
                  defaultEmail={notifyEmail || reminderEmail}
                />
              </div>
            )}
            {!unlocked && !isPassMode && (
              <div className="paywall-upsell" data-testid="paywall-upsell" style={{
                position: "relative",
                zIndex: 5,
                marginTop: -180, // pull up to overlap the blurred polaroid + text
                padding: "28px 24px 26px",
                background: "linear-gradient(180deg, rgba(5,11,27,0.94) 0%, rgba(5,11,27,1) 30%, rgba(5,11,27,1) 100%)",
                borderTop: "1px solid rgba(143, 196, 224, 0.32)",
                borderBottom: "1px solid rgba(143, 196, 224, 0.32)",
                boxShadow: "0 -24px 40px rgba(3,8,23, 0.85)",
                overflow: "hidden",
              }}>
                {/* Conch-shell siren — mirrored watermark behind the SBWU pledge + unlock button */}
                <img
                  src="/siren-logo.png"
                  alt=""
                  aria-hidden="true"
                  data-testid="siren-watermark-paywall"
                  className="siren-illustration"
                  style={{
                    position: "absolute",
                    bottom: -10,
                    left: -20,
                    width: 220,
                    height: "auto",
                    opacity: 0.18,
                    pointerEvents: "none",
                    zIndex: 0,
                    transform: "scaleX(-1)",
                  }}
                />
                <div style={{ position: "relative", zIndex: 1 }}>
                <p className="italic-quote" style={{ fontSize: 22, textAlign: "center", color: "var(--cream)", margin: "0 0 20px", lineHeight: 1.35 }}>
                  The first ripple is always the door.
                </p>
                <p className="body-copy" style={{ textAlign: "center", fontSize: 15, margin: "0 0 16px" }}>
                  What's behind this one: every shape in your cup named and mapped, every symbol woven through the focus you chose, the full reading assembled from what your particular grounds actually held.
                </p>
                <p className="body-copy" style={{ textAlign: "center", fontSize: 15, margin: "0 0 18px" }}>
                  Unlock it and you also get a keepsake — your reading pulled into something you can save and return to — a space to capture what surfaces while it's still alive in you, and three things to carry with you into the next few days.
                </p>
                {/* SBWU pledge — 20% of every unlock goes to Starbucks Workers United */}
                <div
                  data-testid="sbwu-pledge"
                  style={{
                    margin: "0 auto 18px",
                    maxWidth: 340,
                    padding: "12px 14px",
                    background: "rgba(143, 196, 224, 0.06)",
                    border: "1px solid rgba(143, 196, 224, 0.28)",
                    borderRadius: 0,
                    textAlign: "center",
                    fontFamily: "'IM Fell DW Pica', serif",
                    fontStyle: "italic",
                    fontSize: 14,
                    color: "var(--cream-soft)",
                    lineHeight: 1.55,
                  }}
                >
                  <p style={{
                    margin: "0 0 6px",
                    fontFamily: "'Space Mono', monospace",
                    fontStyle: "normal",
                    fontSize: 9,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: "var(--terracotta-soft)",
                  }}>
                    // the siren's pledge
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong style={{ color: "var(--cream)", fontStyle: "normal" }}>20%</strong> of every reading goes to{" "}
                    <a
                      href="https://sbworkersunited.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="sbwu-link"
                      style={{
                        color: "var(--terracotta-soft)",
                        borderBottom: "1px dotted var(--terracotta-soft)",
                        textDecoration: "none",
                        paddingBottom: 1,
                        fontStyle: "normal",
                        fontFamily: "'Space Mono', monospace",
                        fontSize: 12,
                        letterSpacing: "0.06em",
                      }}
                    >
                      Starbucks Workers United
                    </a>
                    {" "}— the union organizing baristas across America. The siren stays with her own.
                  </p>
                </div>
                <p style={{
                  textAlign: "center",
                  fontFamily: "'IM Fell DW Pica', serif",
                  fontStyle: "italic",
                  fontSize: 17,
                  color: "var(--terracotta-soft)",
                  margin: "0 0 6px",
                }}>
                  {CAFE_SLUG ? (
                    <>
                      <span style={{ textDecoration: "line-through", textDecorationThickness: "1.2px", color: "var(--dim)", marginRight: 8 }}>$7.77</span>
                      <span style={{ color: "var(--cream)", fontSize: 20 }}>$5.55</span>
                    </>
                  ) : (
                    <span style={{ color: "var(--cream)", fontSize: 20 }}>$7.77</span>
                  )}
                  <span style={{ display: "block", fontSize: 14, marginTop: 6, color: "var(--cream-soft)" }}>
                    Less than the drink that made this possible.
                  </span>
                </p>
                </div>
              </div>
            )}
            {!unlocked && isPassMode && (
              <div data-testid="pass-reveal-block" style={{ position: "relative", zIndex: 5, marginTop: 24 }}>
                <TerminalQuote
                  text="Same cup, different day. What surfaces this time will be different too — the grounds remember, even when you don't."
                  label="SOMA://LINES.LOG"
                  testId="tidbit-lines"
                />
                <button
                  className="primary-btn"
                  data-testid="pass-reveal-btn"
                  disabled={unlockLoading}
                  onClick={unlockReading}
                  style={{ marginTop: 14, paddingTop: 18, paddingBottom: 18, width: "100%" }}
                >
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, lineHeight: 1.15 }}>
                    <span style={{ fontSize: 19 }}>
                      {unlockLoading ? "Bringing day " + passState.current_day + " into focus…" : "Reveal day " + passState.current_day}
                    </span>
                    {!unlockLoading && (
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bg-1)", opacity: 0.75 }}>
                        included in your pass
                      </span>
                    )}
                  </span>
                </button>
                {unlockError && <p className="error-banner" data-testid="pass-reveal-error" style={{ marginTop: 14 }}>{unlockError}</p>}
              </div>
            )}
            {!unlocked && !isPassMode && (
              <div style={{ position: "relative", zIndex: 5, marginTop: 24 }}>
                <TerminalQuote
                  text="Lines are read as journeys and paths; the clearer and straighter the line, the clearer the road. A wavy line means the way there could wander."
                  label="SOMA://LINES.LOG"
                  testId="tidbit-lines"
                />
                <button
                  className="primary-btn"
                  data-testid="unlock-btn"
                  disabled={unlockLoading}
                  onClick={unlockReading}
                  style={{ marginTop: 14, paddingTop: 18, paddingBottom: 18 }}
                >
                  {unlockLoading ? (
                    paymentPolling ? "Bringing it into focus…" : "Opening checkout…"
                  ) : (
                    <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, lineHeight: 1.15 }}>
                      <span style={{ fontSize: 19 }}>Unlock the full reading</span>
                      <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        {CAFE_SLUG ? (
                          <>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color: "var(--rust)", textDecoration: "line-through", textDecorationThickness: "1.5px", opacity: 0.8 }}>$7.77</span>
                            <span style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 22, color: "var(--bg-1)", fontWeight: 500 }}>$5.55</span>
                          </>
                        ) : (
                          <span style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 22, color: "var(--bg-1)", fontWeight: 500 }}>$7.77</span>
                        )}
                      </span>
                    </span>
                  )}
                </button>
                {CAFE_SLUG && !unlockLoading && (
                  <p
                    data-testid="discount-attribution"
                    style={{
                      textAlign: "center",
                      marginTop: 10,
                      fontFamily: "'IM Fell DW Pica', serif",
                      fontStyle: "italic",
                      fontSize: 14,
                      color: "var(--terracotta-soft)",
                      letterSpacing: "0.01em",
                    }}
                  >
                    a $2.22 discount, washed in by{" "}
                    <span style={{ color: "var(--cream)", fontStyle: "normal", fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: "1px dotted var(--terracotta-soft)", paddingBottom: 1 }}>
                      {CAFE_NAME}
                    </span>
                  </p>
                )}
                {unlockError && <p className="error-banner" style={{ marginTop: 14 }}>{unlockError}</p>}
              </div>
            )}
          </FadeSection>
        </div>
      )}

      {/* 8. SHARE */}
      {unlocked && (
        <div ref={refs.share}>
          <FadeSection id="sec-share" testId="sec-share" className="section-compact">
            <div style={{ display: "flex", gap: 10 }}>
              <button className="primary-btn" data-testid="share-btn" onClick={handleShare} style={{ flex: 1 }}>Share</button>
              <button className="ghost-btn" data-testid="save-btn" onClick={downloadShare} style={{ flex: 1, marginTop: 0 }}>Save</button>
            </div>
            <button
              data-testid="send-to-someone-btn"
              onClick={handleSendToSomeone}
              className="ghost-btn"
              style={{
                marginTop: 12,
                background: "rgba(143, 196, 224, 0.06)",
                borderColor: "var(--terracotta-soft)",
                color: "var(--cream)",
              }}
            >
              Send to someone you're thinking of
            </button>
            <ScrollCue label="stay a moment longer" testId="cue-share" onClick={() => scrollTo("reflect")} />
          </FadeSection>
        </div>
      )}

      {/* 9. REFLECT */}
      {unlocked && (
        <div ref={refs.reflect}>
          <FadeSection id="sec-reflect" testId="sec-reflect">
            <p className="section-marker">08 — what did this stir up?</p>
            {/* Three words AFTER — show the shift */}
            {wordsBefore.some(w => w.trim()) && (
              <div style={{ marginTop: 12, marginBottom: 26 }} data-testid="threewords-after-wrap">
                <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 17, textAlign: "center", color: "var(--cream-soft)", margin: "0 0 10px" }}>
                  And three words now?
                </p>
                <ThreeWords value={wordsAfter} onChange={setWordsAfter} testIdPrefix="threewords-after" />
                {wordsAfter.some(w => w.trim()) && (
                  <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textAlign: "center", color: "var(--terracotta-soft)", margin: "12px 0 0", textTransform: "uppercase" }}>
                    arrived as · <span style={{ color: "var(--cream)" }}>{wordsBefore.filter(w => w.trim()).join(" · ")}</span><br />
                    leaving as · <span style={{ color: "var(--cream)" }}>{wordsAfter.filter(w => w.trim()).join(" · ")}</span>
                  </p>
                )}
              </div>
            )}
            <p className="body-copy" style={{ textAlign: "center", marginTop: 18, marginBottom: 22, fontSize: 14 }}>
              We love hearing how a reading lands. Some reflections we share (only with your okay first) — and every so often, we surprise someone with their reading on us.
            </p>
            <div className="toggle-row" style={{ marginBottom: 18 }}>
              <button
                className={`toggle-btn ${reflectMode === "text" ? "active" : ""}`}
                data-testid="reflect-toggle-text"
                onClick={() => setReflectMode("text")}
              >
                Write
              </button>
              <button
                className={`toggle-btn ${reflectMode === "voice" ? "active" : ""}`}
                data-testid="reflect-toggle-voice"
                onClick={() => setReflectMode("voice")}
              >
                Voice memo
              </button>
            </div>
            {reflectMode === "text" ? (
              <textarea
                className="reflect-textarea"
                data-testid="reflect-textarea"
                rows="5"
                placeholder="What came up for you…"
                value={reflectionText}
                onChange={(e) => setReflectionText(e.target.value)}
              />
            ) : (
              <div>
                <button
                  className={`record-btn ${isRecording ? "recording" : ""}`}
                  data-testid="record-btn"
                  onClick={toggleRecording}
                >
                  {isRecording ? <><Square size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 8 }} /> Tap to stop</> : <><Mic size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 8 }} /> Tap to record</>}
                </button>
                {recordedBlobUrl && (
                  <audio controls src={recordedBlobUrl} style={{ width: "100%", marginTop: 12 }} data-testid="voice-playback" />
                )}
              </div>
            )}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "var(--muted)", marginTop: 16, lineHeight: 1.5 }} data-testid="share-consent-row">
              <input
                type="checkbox"
                checked={shareConsent}
                onChange={(e) => setShareConsent(e.target.checked)}
                data-testid="share-consent-checkbox"
                style={{ marginTop: 3 }}
              />
              <span>It's okay to share this publicly if you pick it as a favorite</span>
            </label>
            {shareConsent && (
              <div style={{ marginTop: 14 }}>
                <input
                  type="email"
                  className="email-input"
                  data-testid="notify-email"
                  placeholder="your email, if you'd like to know ♡"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                />
              </div>
            )}
            <button
              className="primary-btn"
              data-testid="submit-reflection-btn"
              style={{ marginTop: 22 }}
              disabled={reflectionSent}
              onClick={submitReflection}
            >
              {reflectionSent ? "Thank you — we read every one ✨" : "Submit reflection"}
            </button>
          </FadeSection>
        </div>
      )}

      {/* 10. HISTORY */}
      {unlocked && (
        <div ref={refs.history}>
          <FadeSection id="sec-history" testId="sec-history">
            <p className="section-marker">09 — a small piece of history</p>
            <p className="body-copy" style={{ textAlign: "center", marginTop: 22 }}>In the old parlors, no one read a cup just once.</p>
            <p className="body-copy" style={{ textAlign: "center" }}>
              The readers believed a single cup was a glimpse — true, but partial. The shapes that mattered most were the ones that came back. A symbol that appeared again and again, across many cups, was thought to be the one trying hardest to be seen.
            </p>
            <p className="italic-quote" style={{ textAlign: "center", fontSize: 20, color: "var(--cream)", margin: "22px auto", maxWidth: 340, lineHeight: 1.5 }}>
              So the practice was never one cup. It was the habit of looking.
            </p>
            <TerminalQuote
              text={`Much of what we know was gathered in 1881, in a little book called "Reading Tea Leaves," written by someone who signed it only "A Highland Seer." We still don't know their name.`}
              label="SOMA://1881.LOG"
              testId="tidbit-history"
            />
            <ScrollCue label="keep going" testId="cue-history" onClick={() => scrollTo("keepsake")} />
          </FadeSection>
        </div>
      )}

      {/* 11. KEEPSAKE */}
      {unlocked && (
        <div ref={refs.keepsake}>
          <FadeSection id="sec-keepsake" testId="sec-keepsake">
            <p className="section-marker" style={{ textAlign: "center" }}>10 — something to carry</p>
            <div className="paper-card" style={{ marginTop: 18, position: "relative" }}>
              <span className="wax-seal">SL</span>
              <p className="paper-card-title">A pull-quote, for the road</p>
              <p
                className="italic-quote"
                data-testid="keepsake-quote"
                style={{ fontSize: 25, textAlign: "center", color: "var(--ink)", margin: "22px auto 14px", maxWidth: 340, lineHeight: 1.4 }}
              >
                "{pullQuote || readingText}"
              </p>
              <p style={{ textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.3em", color: "var(--rust)", margin: 0, textTransform: "uppercase" }}>
                Soma · Lixir · Vol. I
              </p>
            </div>
            <ScrollCue label="one last thing" testId="cue-keepsake" onClick={() => scrollTo("upsell")} />
          </FadeSection>
        </div>
      )}

      {/* 12. UPSELL — swapped for a "day complete" card when the user is on a pass */}
      {unlocked && isPassMode && passState && (
        <div ref={refs.upsell}>
          <FadeSection id="sec-upsell" testId="sec-upsell-pass">
            <p className="section-marker">
              11 — day {passState.last_completed_day || passState.current_day} complete
            </p>
            <p style={{ textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.3em", color: "var(--terracotta-soft)", margin: "10px 0 6px", textTransform: "uppercase" }}>
              · Volume II · day {String(passState.last_completed_day || passState.current_day).padStart(2, "0")} of {passState.total_days} ·
            </p>
            <h2 className="italic-quote" style={{ fontSize: 30, textAlign: "center", color: "var(--cream)", margin: "10px 0 18px", lineHeight: 1.2 }}>
              {passState.readings.length >= passState.total_days
                ? "the whole month is yours."
                : "see you tomorrow."}
            </h2>
            {passState.readings.length < passState.total_days ? (
              <>
                <p className="body-copy" style={{ textAlign: "center" }}>
                  {passState.total_days - passState.readings.length} day{passState.total_days - passState.readings.length === 1 ? "" : "s"} left. Come back after your next cup — the door opens twenty hours from now.
                </p>
                <p className="body-copy" style={{ textAlign: "center", color: "var(--gold)", fontStyle: "italic", fontSize: 15 }}>
                  The shapes will change. The ones that keep returning are yours to notice.
                </p>
              </>
            ) : (
              <p className="body-copy" style={{ textAlign: "center" }}>
                Twenty-eight cups, twenty-eight noticings. The library below is yours to keep — thank you for staying with it.
              </p>
            )}
            {/* Day 28 — the ARC keepsake ("your month, in one thread") */}
            {passState.readings.length >= passState.total_days && (
              <div data-testid="pass-arc-block" style={{ marginTop: 30 }}>
                {!arcData && (
                  <div style={{ textAlign: "center" }}>
                    <p className="body-copy" style={{ color: "var(--gold)", fontStyle: "italic", fontSize: 15, marginBottom: 14 }}>
                      One last thing — the shape of the month, if you want to see it.
                    </p>
                    <button
                      type="button"
                      className="primary-btn"
                      data-testid="pass-arc-reveal-btn"
                      onClick={revealArc}
                      disabled={arcLoading}
                      style={{ paddingTop: 16, paddingBottom: 16 }}
                    >
                      {arcLoading ? "stitching your thread…" : "reveal your arc"}
                    </button>
                    {arcError && <p className="error-banner" data-testid="pass-arc-error" style={{ marginTop: 14 }}>{arcError}</p>}
                  </div>
                )}
                {arcData && (
                  <div data-testid="pass-arc-content" className="paper-card" style={{ marginTop: 20, position: "relative", padding: "30px 26px" }}>
                    <span className="wax-seal">✦</span>
                    <p className="paper-card-title" style={{ textAlign: "center" }}>the arc</p>
                    <h3
                      data-testid="pass-arc-title"
                      style={{
                        margin: "12px 0 20px",
                        fontFamily: "'IM Fell DW Pica', serif",
                        fontStyle: "italic",
                        fontSize: 26,
                        textAlign: "center",
                        color: "var(--ink)",
                        lineHeight: 1.2,
                      }}
                    >
                      {arcData.title}
                    </h3>
                    <div
                      data-testid="pass-arc-text"
                      style={{
                        fontFamily: "'Georgia', serif",
                        fontSize: 16,
                        lineHeight: 1.7,
                        color: "var(--ink)",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {arcData.arc_text}
                    </div>
                    {arcData.closing_line && (
                      <p
                        data-testid="pass-arc-closing"
                        style={{
                          marginTop: 24,
                          paddingTop: 20,
                          borderTop: "1px solid rgba(11, 20, 32, 0.15)",
                          fontFamily: "'IM Fell DW Pica', serif",
                          fontStyle: "italic",
                          fontSize: 18,
                          textAlign: "center",
                          color: "var(--ink)",
                          lineHeight: 1.4,
                        }}
                      >
                        {arcData.closing_line}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="keepsake-map" data-testid="pass-completion-map" style={{ marginTop: 20 }}>
              {Array.from({ length: passState.total_days }).map((_, i) => {
                const dayNum = i + 1;
                const done = passState.readings.some((r) => r.day === dayNum);
                return <span key={i} className={`map-dot ${done ? "map-dot-active" : ""}`} />;
              })}
            </div>
            {/* Library of past days */}
            {passState.readings.length > 0 && (
              <div data-testid="pass-full-library" style={{ marginTop: 34 }}>
                <p className="section-marker" style={{ marginBottom: 12 }}>your journey so far</p>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                  {[...passState.readings].sort((a, b) => a.day - b.day).map((r) => (
                    <li
                      key={r.day}
                      data-testid={`pass-library-day-${r.day}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 14px",
                        background: "rgba(143, 196, 224, 0.05)",
                        border: "1px solid rgba(143, 196, 224, 0.20)",
                      }}
                    >
                      <span style={{ fontFamily: "'VT323', monospace", fontSize: 22, color: "var(--terracotta-soft)", marginRight: 12 }}>
                        {String(r.day).padStart(2, "0")}
                      </span>
                      <p style={{ margin: 0, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 15, color: "var(--cream)", lineHeight: 1.45, flex: 1 }}>
                        {r.pull_quote ? `"${r.pull_quote}"` : (r.cup_name || "(no quote captured)")}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ margin: "36px 0 0", textAlign: "center" }}>
              <button
                type="button"
                onClick={() => {
                  try { localStorage.removeItem(VolumeIIPanel.TOKEN_KEY); } catch { /* ignore */ }
                  setVolumeIIToken(null);
                  setPassState(null);
                  setPassJustCompleted(false);
                }}
                data-testid="pass-signout-btn"
                className="mono-btn"
                style={{ padding: "10px 16px" }}
              >
                sign out of this device
              </button>
            </p>
            <p style={{
              textAlign: "center",
              fontFamily: "'Space Mono', monospace",
              fontSize: 9,
              letterSpacing: "0.34em",
              color: "var(--dim)",
              textTransform: "uppercase",
              marginTop: 36,
              paddingTop: 24,
              borderTop: "1px solid rgba(143, 196, 224, 0.18)",
              opacity: 0.75,
            }}>
              // volume ii · v0.1 · drinksomalixir.com
            </p>
          </FadeSection>
        </div>
      )}

      {/* 12. UPSELL */}
      {unlocked && !isPassMode && (
        <div ref={refs.upsell}>
          <FadeSection id="sec-upsell" testId="sec-upsell">
            <p className="section-marker">11 — if today felt good</p>
            <p style={{ textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.3em", color: "var(--terracotta-soft)", margin: "10px 0 6px", textTransform: "uppercase" }}>
              · Volume II ·
            </p>
            <h2 className="italic-quote" style={{ fontSize: 30, textAlign: "center", color: "var(--cream)", margin: "10px 0 18px", lineHeight: 1.2 }}>
              the twenty-eight-day practice
            </h2>
            <p className="body-copy" style={{ textAlign: "center" }}>
              Twenty-eight mornings. Five minutes each. A cup in your hands and a question worth sitting with, before the day asks anything of you.
            </p>
            <p className="body-copy" style={{ textAlign: "center" }}>
              The shapes will change. The focus will shift. And somewhere in the middle, you'll start to catch the ones that keep returning — in your cup, and in yourself. That's the part a single reading can never give you.
            </p>
            <button
              className="primary-btn"
              data-testid="buy-pass-btn"
              style={{ marginTop: 22 }}
              disabled={passLoading}
              onClick={beginVolumeII}
            >
              <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, lineHeight: 1.15 }}>
                <span style={{ fontSize: 18 }}>{passLoading ? "Opening checkout…" : "Begin Volume II"}</span>
                {!passLoading && (
                  <span style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 22, color: "var(--bg-1)" }}>$11.11</span>
                )}
              </span>
            </button>
            {passError && <p className="error-banner" data-testid="pass-error" style={{ marginTop: 10 }}>{passError}</p>}
            <p className="brand-footer" style={{ marginTop: 28, marginBottom: 12 }}>Day 01 of 28 · Vol. II</p>
            <div className="keepsake-map" data-testid="keepsake-map">
              {Array.from({ length: 28 }).map((_, i) => (
                <span key={i} className={`map-dot ${i === 0 ? "map-dot-active" : ""}`} />
              ))}
            </div>
            <p className="italic-quote" style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, marginTop: 22 }}>
              We'll be here every day, if you want to keep going.
            </p>
            <p className="body-copy" style={{ textAlign: "center", color: "var(--gold)", fontSize: 13, fontStyle: "italic", marginTop: 16 }}>
              On the final morning, one last reading gathers the whole month together — including the shapes that kept finding their way back to you.
            </p>
            {/* Email reminder opt-in */}
            <div style={{ marginTop: 36 }} data-testid="reminder-opt-in-wrap">
              <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.24em", color: "var(--terracotta-soft)", textTransform: "uppercase", textAlign: "center", margin: "0 0 10px" }}>
                // a small wave tomorrow?
              </p>
              <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 16, textAlign: "center", color: "var(--cream-soft)", margin: "0 0 14px", lineHeight: 1.5 }}>
                If today felt good but you're not ready for the whole 28 days, we can send one gentle reminder tomorrow morning. No spam, no list — just one note, one cup.
              </p>
              {!reminderSaved ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="email"
                    className="email-input"
                    data-testid="reminder-email-input"
                    placeholder="your email"
                    value={reminderEmail}
                    onChange={(e) => setReminderEmail(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button
                    data-testid="reminder-save-btn"
                    onClick={saveReminder}
                    disabled={!reminderEmail.includes("@")}
                    className="mono-btn"
                    style={{
                      padding: "0 16px",
                      whiteSpace: "nowrap",
                      opacity: reminderEmail.includes("@") ? 1 : 0.4,
                      cursor: reminderEmail.includes("@") ? "pointer" : "not-allowed",
                    }}
                  >
                    Remind me
                  </button>
                </div>
              ) : (
                <p data-testid="reminder-saved" style={{ textAlign: "center", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 16, color: "var(--terracotta-soft)" }}>
                  Saved. We'll surface again tomorrow morning. 🐚
                </p>
              )}
            </div>
            <p className="body-copy" style={{ textAlign: "center", color: "var(--dim)", fontSize: 12, marginTop: 30 }}>
              Not today? Just close this. Today's cup is already yours to keep.
            </p>
            <p style={{
              textAlign: "center",
              fontFamily: "'Space Mono', monospace",
              fontSize: 9,
              letterSpacing: "0.34em",
              color: "var(--dim)",
              textTransform: "uppercase",
              marginTop: 36,
              paddingTop: 24,
              borderTop: "1px solid rgba(143, 196, 224, 0.18)",
              opacity: 0.75,
            }}>
              // beta · v0.1 · drinksomalixir.com
            </p>
          </FadeSection>
        </div>
      )}
    </div>
  );
}

/* helpers */
function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let current = "";
  for (const word of words) {
    const test = current ? current + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}
